import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ContentfulCloudinaryImage } from '../../../../core/interfaces/Contentful/CloudinaryImage/ContentfulCloudinaryImage';
import { ContentfulImageRendition } from 'src/app/core/interfaces/Contentful/ImageRendition/ContentfulImageRenditionSkeleton';
import { SanitizeContentfulHTMLPipe } from '../../../pipes/sanitize-contentful-html.pipe';
import { SanitizeContentPipe } from '../../../pipes/sanitize-content.pipe';
import { AppContentfulCloudinaryImageComponent } from '../contentful-cloudinary-image/contentful-cloudinary-image.component';
import { AppContentfulCloudinaryVideoComponent } from '../contentful-cloudinary-video/contentful-cloudinary-video.component';
import { AppContentfulDisclaimerComponent } from '../contentful-disclaimer/contentful-disclaimer.component';
import { getAssetSysFake } from 'src/app/shared/helpers/contentful.helper';
import { AppContentfulMiniNavigationBlockComponent } from '../contentful-mininavigation-block/contentful-mininavigation-block.component';
import { NgClass } from '@angular/common';
import { ContentfulRichBlockContent } from 'src/app/core/interfaces/Contentful/RichBlock';
import { ContentfulSectionContent } from 'src/app/core/interfaces/Contentful/ContentfulSectionContent';
import { ContentfulBaseService } from 'src/app/shared/services/contentful-base.service';

@Component({
  selector: 'app-contentful-rich-text-block',
  templateUrl: './contentful-rich-text-block.component.html',
  styleUrls: ['./contentful-rich-text-block.component.scss'],
  standalone: true,
  imports: [
    AppContentfulMiniNavigationBlockComponent,
    AppContentfulCloudinaryImageComponent,
    AppContentfulCloudinaryVideoComponent,
    AppContentfulDisclaimerComponent,
    SanitizeContentfulHTMLPipe,
    SanitizeContentPipe,
    NgClass,
  ],
})
export class AppContentfulRichTextBlockComponent implements OnChanges {
  @Input() contentfulSectionContent: ContentfulSectionContent | undefined;
  contentfulRichBlockContents: Array<ContentfulRichBlockContent> | undefined;
  contentfulRichBlockContent$ = new BehaviorSubject<ContentfulRichBlockContent[]>([]);
  contentfulCloudinaryImage: Array<ContentfulCloudinaryImage> = [];
  contentfulCloudinaryVideoUrl: Array<string> = [];
  contentfulCloudinaryVideoMediaType: Array<string> = [];
  contentfulDisclaimerTexts: Array<string> = [];
  contentfulDisclaimerTextsMediaType: Array<string> = [];
  #contentfulBaseService = inject(ContentfulBaseService);

  ngOnChanges(changes: SimpleChanges): void {
    const currentValue = changes.contentfulSectionContent.currentValue;
    const contentfulSectionBlocksContent = currentValue.contentfulContent?.content;

    if (contentfulSectionBlocksContent && currentValue && contentfulSectionBlocksContent.contents) {
      this.contentfulRichBlockContents = contentfulSectionBlocksContent.contents;

      // Preprocess the content here to avoid multiple calls using methods from HTML
      this.processContent();

      this.contentfulRichBlockContent$.next(contentfulSectionBlocksContent.contents);
    }
  }

  /**
   Preprocess all the html content
   */
  processContent(): void {
    if (this.contentfulRichBlockContents) {
      this.contentfulRichBlockContents.map((block) => {
        block.content.map((content, index) => {
          switch (content.nodeType) {
            case 'paragraph':
              content.processedContent = this.getParagraphContent(content);
              break;
            case 'unordered-list':
              content.processedContent = this.getListItemContent(content);
              break;
            case 'heading-2':
            case 'heading-3':
              content.processedContent = this.getHeadingContent(content);
              break;
            case 'embedded-entry-block':
              if (content.content[0]) {
                const embeddedEntryBlockType = content.content[0].value;
                if (embeddedEntryBlockType === 'image') {
                  const imageRendition: ContentfulImageRendition = {
                    name: 'image-rendition',
                    aspectRatio: 0,
                    transformations: '',
                    width: 800,
                    height: 800,
                  };
                  // Fake asset sys object needed to create a contentfulCloudinaryImage object
                  const assetSysFake = getAssetSysFake();
                  // We need to create a contentfulCloudinaryImage object to give it to the contentfulCloudinaryImage component
                  // FIXME: The creation of this object by assigning empty data indicates that this is done to avoid errors. This is a patch that we should avoid. Analyze it and correct it
                  const cloudinaryImage: ContentfulCloudinaryImage = {
                    alternateText: content.imageId || '',
                    imageRendition,
                    isPreload: false,
                    name: content.imageId || '',
                    asset: {
                      sys: assetSysFake.sys,
                      metadata: assetSysFake.metadata,
                      fields: {
                        file: {
                          contentType: content.content[0].value || '',
                          fileName: content.imageId || '',
                          url:
                            content.content[0].data.uri ??
                            this.#contentfulBaseService.getDefaultImageAssetUrl(
                              'No cloudinary image found for embedded-entry-block.',
                            ),
                          details: assetSysFake.fields.file!.details, // File always exists in assetSysFake. Non-null assertion is safe
                        },
                        title: content.imageId || '',
                      },
                    },
                  };
                  this.contentfulCloudinaryImage[index] = cloudinaryImage;
                } else if (embeddedEntryBlockType === 'video') {
                  this.contentfulCloudinaryVideoUrl[index] = content.content[0].data.uri;
                  this.contentfulCloudinaryVideoMediaType[index] = content.content[0].value;
                } else if (embeddedEntryBlockType === 'disclaimer') {
                  this.contentfulDisclaimerTexts[index] = content.content[0].disclaimerText;
                  this.contentfulDisclaimerTextsMediaType[index] = content.content[0].value;
                }
              }
              break;
          }
        });
      });
    }
  }

  trackByContentfulRichBlockContent(item: any): string {
    return item.anchorId;
  }

  /**
   Preprocess and returns all the heading html content
   */
  getHeadingContent(content: {
    nodeType: string;
    content: { anchorId?: string; value: string; marks: { type: string }[] }[];
  }): string {
    const tag = content.nodeType === 'heading-3' ? 'h3' : 'h2';

    const value = content.content
      .map((item) => {
        const anchorId = item.anchorId ? ` id="${item.anchorId}"` : '';
        // TODO: If a new mark.type is found, add it to the component stylesheet to ensure proper text rendering
        const classItem = item.marks?.length > 0 ? ` class="mark-${item.marks.map((mark) => mark.type).join(' ')}"` : '';

        return `<span${anchorId}${classItem}>${item.value}</span>`;
      })
      .join('');

    return `<${tag}>${value}</${tag}>`;
  }

  /**
   Preprocess and returns all the paragraph text html content (including hyperlinks)
   */
  getParagraphContent(textContent: { content: any }): string {
    return textContent.content
      .map((paragraphContent: { nodeType: string; content: { value: string }[]; data: { uri: string } }) => {
        if (paragraphContent.nodeType === 'text') {
          return this.checkParagraphTextIsBold(paragraphContent);
        } else if (paragraphContent.nodeType === 'hyperlink') {
          // Remove blank spaces to avoid detecting them as unsafe url when sanitizing content
          let url = paragraphContent.data.uri.replaceAll(' ', '');
          return `<a class="text-decoration-underline" href="${url}">${paragraphContent.content[0].value}</a>`;
        }
        return '';
      })
      .join('');
  }

  /**
   Checks if the API info comes with style and applies it in that case
   */
  checkParagraphTextIsBold(paragraphContent: any) {
    if (paragraphContent.marks[0] && paragraphContent.marks[0].type) {
      if (paragraphContent.marks[0].type === 'bold') {
        return `<b>${paragraphContent.value}</b>`;
      }
    } else {
      return paragraphContent.value;
    }
  }

  /**
   Preprocess and returns all the unordered list html content
   */
  getListItemContent(listItem: any): string {
    return listItem.content.reduce((result: string, element: { nodeType: string; content: any }) => {
      if (element.nodeType === 'list-item') {
        const paragraphContent = element.content
          .map((paragraphElement: any) => this.getParagraphContent(paragraphElement))
          .join('');
        result += `<li>${paragraphContent}</li>`;
      }
      return result;
    }, '');
  }
}
